import React, { useState, useEffect, useCallback, useRef } from 'react';
import CalendarGrid from './components/CalendarGrid';
import DayModal from './components/DayModal';
import Confetti from './components/Confetti';
import StarIcon from './components/StarIcon';
import LoginPopup from './components/LoginPopup';
import UserAvatar from './components/UserAvatar';
import BadgeSlots from './components/BadgeSlots';
import BadgePopup from './components/BadgePopup';
import ScorePopup from './components/ScorePopup';
import InfoPopup from './components/InfoPopup';
import LinkInventory from './components/LinkInventory';
import CalendarChoiceScreen from './components/CalendarChoiceScreen';
import { clearAllCompletions, getScore, getTotalStars, getDayScore, getDayStars, saveBadges, checkForNewBadges, INFO_POPUP_WELCOME_KEY, INFO_POPUP_LAST_DAY_KEY } from './utils/completionStorage';
import { BadgeType, BadgeTier } from './utils/badges';
import { checkAuthStatus, handleTokenFromURL, AuthUser, getAuthHeaders } from './utils/auth';
import { initGA, trackPageView, trackModalOpen, trackBadgeEarned } from './utils/analytics';
import { isMuted, setMuted, sound } from './utils/sound';
import { debugLog, debugError } from './utils/debug';
import { initializeTheme, THEME_STORAGE_KEY, DEFAULT_SKIN } from './utils/themeLoader';

interface CalendarConfig {
  companyGameId: number;
  calendar: {
    year: number;
    month: number;
    startDay: number;
    startColumn?: number;
    daysPerRow: number;
    title: string;
    description: string;
  };
  dayData: {
    availableDays: number[];
    specialDays: {
      [key: number]: {
        type: string;
        count?: number;
      };
    };
  };
}

interface DayContent {
  day: number;
  title: string;
  contentType: string;
  content: string | object;
}

interface ScoreBreakdown {
  relevance: number;
  correctness: number;
  depth: number;
  clarity: number;
  promptcraft: number;
}

interface NewBadge {
  type: BadgeType;
  tier: BadgeTier;
}

interface BonusData {
  totalPoints: number;
  stars?: number;
  scoreBreakdown?: ScoreBreakdown;
  scoreDelta?: number;
  addedPoints?: number; // The actual points added to total score
  isImprovement?: boolean; // Whether this is an improvement over previous submission
  previousBreakdown?: ScoreBreakdown | null; // Previous breakdown for comparison
  newBadges?: NewBadge[]; // Newly earned badges
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';
const SHOW_CHOICE_SCREEN_SINGLE = false; // Show choice screen even if only 1 game available

interface CompanyGame {
  companyGameId: number;
  game: {
    slug: string;
    name: string;
  };
  cta: string;
  skin?: string;
}

function App() {
  // Theme loading state
  const [themeLoaded, setThemeLoaded] = useState(false);
  const [themeError, setThemeError] = useState<string | null>(null);
  const [currentSkin, setCurrentSkin] = useState<string | null>(null);

  // Company games and selection state
  const [companyGames, setCompanyGames] = useState<CompanyGame[]>([]);
  const [selectedCompanyGameId, setSelectedCompanyGameId] = useState<number | null>(null);
  const [showChoiceScreen, setShowChoiceScreen] = useState(false);
  const [hasChoiceScreen, setHasChoiceScreen] = useState(false); // Track if choice screen was ever shown

  // Track if we pushed a browser history entry for back button support
  const hasBrowserHistory = useRef(false);

  const [config, setConfig] = useState<CalendarConfig | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [dayContent, setDayContent] = useState<DayContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSelectedDayCurrentDay, setIsSelectedDayCurrentDay] = useState(false);
  const [confettiKey, setConfettiKey] = useState(0);
  const [shouldCloseModal, setShouldCloseModal] = useState(false);
  const [score, setScore] = useState(0);
  const [displayScore, setDisplayScore] = useState(0);
  const [scoreAnimating, setScoreAnimating] = useState(false);
  const [starsAnimating, setStarsAnimating] = useState(false);
  const [badgesAnimating, setBadgesAnimating] = useState(false);
  const [totalStars, setTotalStars] = useState(0);
  const [displayStars, setDisplayStars] = useState(0);
  const [starsPulseKey, setStarsPulseKey] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [showBadgePopup, setShowBadgePopup] = useState(false);
  const [newBadges, setNewBadges] = useState<NewBadge[]>([]);
  const [isBadgePopupClosing, setIsBadgePopupClosing] = useState(false);
  const [badgesToAnimate, setBadgesToAnimate] = useState<NewBadge[]>([]);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [scorePopupData, setScorePopupData] = useState<BonusData | null>(null);
  const [scorePopupDay, setScorePopupDay] = useState<number | null>(null);
  const [scorePopupToday, setScorePopupToday] = useState(false);

  // Chat sessions per day (persists during session, resets on submission)
  const [chatSessions, setChatSessions] = useState<{
    [dayNumber: number]: {
      messages: Array<{ role: 'user' | 'assistant'; content: string }>;
      conversationId: number | null;
    }
  }>({});
  const [isScorePopupClosing, setIsScorePopupClosing] = useState(false);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [infoPopupType, setInfoPopupType] = useState<'welcome' | 'lastDay'>('welcome');
  const [isInfoPopupClosing, setIsInfoPopupClosing] = useState(false);
  const [serverDate, setServerDate] = useState<Date | null>(null);
  const [muted, setMutedState] = useState(isMuted());
  const [showLinkInventory, setShowLinkInventory] = useState(false);
  const [linkInventoryVideos, setLinkInventoryVideos] = useState<any>(null);
  const [linkInventoryLoading, setLinkInventoryLoading] = useState(false);


  // Log .env variables for debugging
  // console.log('[App] .env variables:', {
  //   REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  //   NODE_ENV: process.env.NODE_ENV,
  //   PUBLIC_URL: process.env.PUBLIC_URL,
  //   allEnvVars: process.env
  // });

  // Session cache for content API responses (day -> content data)
  const contentCacheRef = React.useRef<Map<number, any>>(new Map());
  // Session cache for videos API response
  const videosCacheRef = React.useRef<any>(null);

  // Fetch current date from server
  const fetchCurrentDate = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/current-date`);
      const responseData = await response.json();
      const data = responseData.data || responseData;

      // DEBUG TEST OVERRIDE
      // data.currentDate = '2025-11-24'; // TEST OVERRIDE

      const date = new Date(data.currentDate);

      date.setUTCHours(12, 0, 0, 0); // Normalize time to mid-day GMT
      setServerDate(date);
      debugLog(1, '[App] Server date:', data.currentDate, date);
      return date;
    } catch (error) {
      debugError('[App] Error fetching current date:', error);
      return null;
    }
  }, [setServerDate]);

  // Fetch user submissions from server
  const fetchUserSubmissions = useCallback(async (companyGameId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/submissions/my?companyGameId=${companyGameId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const submissions = await response.json();
        debugLog(1, '[App] User submissions (raw):', submissions);

        // Process submissions: keep only the highest ai_score for each day
        const submissionsByDay = new Map<number, any>();

        submissions.forEach((submission: any) => {
          // Skip submissions without AI scoring data
          if (submission.ai_score == null && submission.ai_stars == null && submission.ai_score_breakdown == null) {
            debugLog(1, `[App] Skipping day ${submission.day} - no AI scoring data`);
            return;
          }

          // If no stars, zero out score as well
          if (submission.ai_stars === 0)
            submission.ai_score = 0;
          // TEST
          // else
          //   submission.ai_stars = 3;////

          const day = submission.day;
          const existingSubmission = submissionsByDay.get(day);

          // Keep submission with highest ai_score
          if (!existingSubmission || submission.ai_score > existingSubmission.ai_score) {
            submissionsByDay.set(day, submission);
          }
        });

        // Convert to array of best submissions per day
        const bestSubmissions = Array.from(submissionsByDay.values());
        debugLog(1, '[App] Best submissions per day (from server):', bestSubmissions);

        // Merge with locally stored submissions
        const localSubmissionsData = localStorage.getItem('advent_calendar_day_submissions');
        const localSubmissions = localSubmissionsData ? JSON.parse(localSubmissionsData) : {};
        debugLog(1, '[App] Local submissions:', localSubmissions);

        // Create merged submissions: keep higher score for each day
        const mergedSubmissions = new Map<number, any>();

        // First, add all server submissions
        bestSubmissions.forEach((serverSubmission: any) => {
          // Parse feedback if it exists (it's a JSON string)
          let feedback = null;
          if (serverSubmission.ai_feedback) {
            try {
              feedback = JSON.parse(serverSubmission.ai_feedback);
            } catch (error) {
              debugError(`[App] Failed to parse feedback for day ${serverSubmission.day}:`, error, serverSubmission);
            }
          }

          mergedSubmissions.set(serverSubmission.day, {
            day: serverSubmission.day,
            source: 'server',
            score: serverSubmission.ai_score,
            stars: serverSubmission.ai_stars,
            scoreBreakdown: serverSubmission.ai_score_breakdown,
            feedback: feedback,
            today: serverSubmission.today || false,
            serverData: serverSubmission
          });
        });

        // Then, compare with local submissions
        Object.keys(localSubmissions).forEach((dayKey) => {
          const day = parseInt(dayKey);
          const localData = localSubmissions[day];
          const serverEntry = mergedSubmissions.get(day);

          if (!serverEntry) {
            // Local submission exists but no server submission - keep local
            mergedSubmissions.set(day, {
              day: day,
              source: 'local',
              score: localData.score,
              stars: localData.stars,
              scoreBreakdown: localData.scoreBreakdown,
              feedback: localData.feedback,
              today: false, // Local data doesn't have server's today field
              localData: localData
            });
          } else if (localData.score >= serverEntry.score) {
            // Local score is higher - override with local score breakdown and feedback
            // debugLog(1, `[App] Day ${day}: Local score (${localData.score}) >= Server score (${serverEntry.score}) - using local data`);
            mergedSubmissions.set(day, {
              day: day,
              source: 'local',
              score: localData.score,
              stars: localData.stars,
              scoreBreakdown: localData.scoreBreakdown,
              feedback: localData.feedback,
              today: serverEntry.today || false, // Keep server's today value even when using local score
              localData: localData
            });
          } else {
            debugLog(1, `[App] Day ${day}: Server score (${serverEntry.score}) > Local score (${localData.score}) - using server data`);
          }
        });

        const mergedArray = Array.from(mergedSubmissions.values());
        debugLog(1, '[App] Merged submissions:', mergedArray);

        // Calculate total score and stars from merged data
        let totalScore = 0;
        let totalStars = 0;

        mergedArray.forEach((entry: any) => {
          totalScore += entry.score || 0;
          totalStars += entry.stars || 0;
        });

        debugLog(1, '[App] Calculated totals (merged):', {
          totalScore,
          totalStars,
          daysCompleted: mergedArray.length
        });

        // Check if merged score is higher than current stored score
        const currentStoredScore = getScore();
        const currentStoredStars = getTotalStars();

        debugLog(1, '[App] Comparing scores - Current stored:', currentStoredScore, 'Merged:', totalScore);
        debugLog(1, '[App] Comparing stars - Current stored:', currentStoredStars, 'Merged:', totalStars);

        if (totalScore > currentStoredScore) {
          debugLog(1, '[App] Merged data has better score - syncing to local storage');

          // 1. Override local storage with merged submission data
          const submissionsToStore: any = {};
          mergedArray.forEach((entry: any) => {
            submissionsToStore[entry.day] = {
              score: entry.score,
              stars: entry.stars,
              scoreBreakdown: entry.scoreBreakdown,
              feedback: entry.feedback
            };
          });
          localStorage.setItem('advent_calendar_day_submissions', JSON.stringify(submissionsToStore));
          debugLog(1, '[App] Updated day submissions in local storage');

          // 2. Override total score
          localStorage.setItem('advent_calendar_score', totalScore.toString());
          debugLog(1, '[App] Updated total score in local storage:', totalScore);

          // 3. Mark days as completed
          const completedDays: any = {};
          mergedArray.forEach((entry: any) => {
            if (entry.stars > 0) {
              completedDays[entry.day] = true;
            }
          });
          localStorage.setItem('advent_calendar_completions', JSON.stringify(completedDays));
          debugLog(1, '[App] Updated completions in local storage');

          // 4. Track on-time completions from 'today' field
          const onTimeCompletions: number[] = [];
          mergedArray.forEach((entry: any) => {
            if (entry.today && entry.stars > 0) {
              onTimeCompletions.push(entry.day);
            }
          });
          localStorage.setItem('advent_calendar_on_time_completions', JSON.stringify(onTimeCompletions));
          debugLog(1, '[App] Updated on-time completions in local storage:', onTimeCompletions);

          // 5. Recalculate badges from merged data
          if (config?.dayData.availableDays) {
            // Calculate taskDays (exclude "off" days from streak calculations)
            const taskDays = config.dayData.availableDays.filter(day => {
              const specialDay = config.dayData.specialDays[day];
              return specialDay?.type !== 'off';
            });
            debugLog(1, '[App] taskDays for badge calculation:', taskDays);

            const badgeResult = checkForNewBadges(taskDays.length, taskDays);
            if (badgeResult.newBadges.length > 0) {
              debugLog(1, '[App] New badges from merged data:', badgeResult.newBadges);
              saveBadges(badgeResult.newBadges);
            } else {
              debugLog(1, '[App] No new badges from merged data');
            }
          }

          // 6. Update state to reflect new values
          setScore(totalScore);
          setDisplayScore(totalScore);
          setTotalStars(totalStars);
          setDisplayStars(totalStars);

          debugLog(1, '[App] Local storage sync complete');

          // 7. Trigger unlock check for any days that may now be unlocked
          // Use a special event to indicate this is from sync (no animations)
          debugLog(1, '[App] Triggering unlock status check from sync...');
          window.dispatchEvent(new CustomEvent('syncCompleted'));
        } else {
          debugLog(1, '[App] Stored data is already up to date, no sync needed');
        }

        return mergedArray;
      } else {
        debugError('[App] Failed to fetch submissions:', response.status);
        return null;
      }
    } catch (error) {
      debugError('[App] Error fetching submissions:', error);
      return null;
    }
  }, [config, setScore, setDisplayScore, setTotalStars, setDisplayStars]);

  const checkAndShowInfoPopup = useCallback(() => {
    // Check if welcome popup has been shown
    const welcomeShown = localStorage.getItem(INFO_POPUP_WELCOME_KEY);

    if (!welcomeShown) {
      setInfoPopupType('welcome');
      setShowInfoPopup(true);
      return true; // Will show popup
    }
    return false; // No popup to show
  }, [setInfoPopupType, setShowInfoPopup]);

  // Initialize Google Analytics on mount
  useEffect(() => {
    initGA();
    trackPageView(window.location.pathname);
  }, []);

  // Check authentication on mount
  useEffect(() => {
    let cancelled = false;

    const initAuth = async () => {
      debugLog(1, '[App] Initializing authentication...');

      // First, check if there's a token in the URL (from OAuth redirect)
      const hadTokenInURL = handleTokenFromURL();
      if (hadTokenInURL) {
        debugLog(1, '[App] Token received from OAuth callback');
      }

      // Then check auth status
      const authStatus = await checkAuthStatus();

      if (cancelled) return;

      if (authStatus.authenticated && authStatus.user) {
        debugLog(1, '[App] User authenticated:', authStatus.user);
        setIsAuthenticated(true);
        setAuthUser(authStatus.user);
      } else {
        debugLog(1, '[App] User not authenticated, showing login popup...');
        setIsAuthenticated(false);
      }

      setAuthChecking(false);
    };

    initAuth();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchCompanyGames = async () => {
    try {
      const response = await fetch(`${API_URL}/api/company-games`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const responseData = await response.json();

        // TEST debug OVERRIDE - remove!!
        // responseData.data = [
        //   {
        //     "companyGameId": 100,
        //     "companyId": 1,
        //     "cta": "continue",
        //     "daysPerRow": 5,
        //     "game": {
        //       "description": "Daily AI skill challenges",
        //       "id": 36,
        //       "name": "AI Skill Advent Calendar",
        //       "slug": "ai-skill-advent-2025"
        //     },
        //     "gameId": 36,
        //     "mode": "global",
        //     "month": 11,
        //     "progress": {
        //       "daysCompleted": 9,
        //       "totalDays": 21
        //     },
        //     "skin": null,
        //     "startDay": 5,
        //     "title": "AI Skill Advent Calendar",
        //     "year": 2025
        //   },
        //   {
        //     "companyGameId": 101,
        //     "companyId": 1,
        //     "cta": "continue",
        //     "daysPerRow": 5,
        //     "game": {
        //       "description": "Daily AI skill challenges",
        //       "id": 36,
        //       "name": "AI Skill Advent Calendar",
        //       "slug": "test"
        //     },
        //     "gameId": 36,
        //     "mode": "global",
        //     "month": 11,
        //     "progress": {
        //       "daysCompleted": 2,
        //       "totalDays": 21
        //     },
        //     "skin": null,
        //     "startDay": 5,
        //     "title": "AI Skill Advent Calendar",
        //     "year": 2025
        //   },          
        // ];

        const data = responseData.data || responseData;
        debugLog(1, '[App] Company games:', data);

        // Store company games in state
        setCompanyGames(data);

        // Determine if we should show the choice screen
        const shouldShowChoice = data.length > 1 || (data.length === 1 && SHOW_CHOICE_SCREEN_SINGLE);
        setHasChoiceScreen(shouldShowChoice); // Track that we had a choice screen

        // Check if URL hash specifies a calendar to open
        const hash = window.location.hash;
        let initialGameId: number | null = null;

        if (hash.startsWith('#calendar')) {
          const gameIdStr = hash.replace('#calendar', '');
          const gameId = parseInt(gameIdStr, 10);

          if (!isNaN(gameId) && gameId > 0) {
            // Validate that this game ID exists in our list
            const gameExists = data.some((g: CompanyGame) => g.companyGameId === gameId);
            if (gameExists) {
              debugLog(1, `[App] Opening calendar ${gameId} from URL hash`);
              initialGameId = gameId;
              setSelectedCompanyGameId(gameId);
              setShowChoiceScreen(false);
              // Don't set hasBrowserHistory = true here, since we didn't push this history entry
              // The user came in with this URL, so back button should show choice screen directly
            } else {
              debugLog(1, `[App] Calendar ${gameId} not found, showing choice screen`);
              setShowChoiceScreen(shouldShowChoice);
              // Clear invalid hash from URL
              window.history.replaceState(null, '', window.location.href.split('#')[0]);
            }
          } else {
            setShowChoiceScreen(shouldShowChoice);
            // Clear invalid hash from URL
            window.history.replaceState(null, '', window.location.href.split('#')[0]);
          }
        } else {
          setShowChoiceScreen(shouldShowChoice);
          // No hash present, ensure URL is clean
          if (window.location.hash) {
            window.history.replaceState(null, '', window.location.href.split('#')[0]);
          }
        }

        debugLog(1, '[App] Show choice screen:', !initialGameId && shouldShowChoice);

        // Load theme from first entry's skin if we're showing the choice screen
        // (not if we opened directly from URL hash)
        if (!initialGameId && shouldShowChoice && data.length > 0) {
          const skinName = data[0].skin || DEFAULT_SKIN;
          debugLog(1, `[App] Loading theme for choice screen with skin: ${skinName}`);

          try {
            await initializeTheme(skinName);
            debugLog(1, '[App] Theme loaded successfully for choice screen');
            setThemeLoaded(true);
            setCurrentSkin(skinName);
          } catch (error) {
            debugError('[App] Failed to load theme for choice screen:', error);
            setThemeError(error instanceof Error ? error.message : 'Unknown error');
            // Still set themeLoaded to true to allow app to render (with fallback styles)
            setThemeLoaded(true);
            setCurrentSkin(skinName);
          }
        }

        // If only 1 game and we're not showing choice screen, auto-select it
        if (data.length === 1 && !SHOW_CHOICE_SCREEN_SINGLE) {
          setSelectedCompanyGameId(data[0].companyGameId);
        }
      } else {
        debugError('[App] Failed to fetch company games:', response.status);
      }
    } catch (error) {
      debugError('[App] Error fetching company games:', error);
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      // Only fetch company games if authenticated
      if (isAuthenticated) {
        // 1. Fetch company games first
        await fetchCompanyGames();
      }
    };

    initializeApp();
  }, [isAuthenticated]);

  // Fetch config after a game is selected
  useEffect(() => {
    const loadConfig = async () => {
      if (selectedCompanyGameId) {
        // Fetch config for the selected game
        await fetchConfig(selectedCompanyGameId);

        // Initialize scores
        const initialScore = getScore();
        const initialStars = getTotalStars();
        setScore(initialScore);
        setDisplayScore(initialScore);
        setTotalStars(initialStars);
        setDisplayStars(initialStars);
      }
    };

    loadConfig();
  }, [selectedCompanyGameId]);

  // Fetch current date and submissions after config is loaded
  useEffect(() => {
    const loadData = async () => {
      if (config) {
        // 2. Fetch current date
        await fetchCurrentDate();

        // 3. Fetch user submissions
        await fetchUserSubmissions(config.companyGameId);
      }
    };

    loadData();
  }, [config, fetchCurrentDate, fetchUserSubmissions]);

  // Check for info popup after config is loaded
  useEffect(() => {
    if (config && serverDate) {
      const willShowPopup = checkAndShowInfoPopup();

      // If no popup will be shown, trigger initial unlock check
      if (!willShowPopup) {
        debugLog(1, '[App] No info popup to show, triggering initial unlock check');
        // Small delay to ensure everything is ready
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('initialLoad'));
        }, 100);
      } else {
        debugLog(1, '[App] Info popup will be shown, deferring unlock check until popup closes');
      }
    }
  }, [config, checkAndShowInfoPopup, serverDate]);

  // Listen for last day arrived event from CalendarGrid
  useEffect(() => {
    const handleLastDayArrived = () => {
      const lastDayShown = localStorage.getItem(INFO_POPUP_LAST_DAY_KEY);

      if (!lastDayShown) {
        setInfoPopupType('lastDay');
        setShowInfoPopup(true);
      }
    };

    window.addEventListener('lastDayArrived', handleLastDayArrived);
    return () => {
      window.removeEventListener('lastDayArrived', handleLastDayArrived);
    };
  }, []);

  // Animate score counting up
  useEffect(() => {
    if (displayScore < score) {
      const increment = 100;
      const timer = setInterval(() => {
        setDisplayScore(prev => {
          const next = prev + increment;
          if (next >= score) {
            clearInterval(timer);
            return score;
          }
          return next;
        });
      }, 5); // Update interval

      return () => clearInterval(timer);
    }
  }, [score, displayScore]);

  // Animate stars counting up after score finishes
  useEffect(() => {
    if (displayScore >= score && displayStars < totalStars) {
      // Trigger stars section animation
      setStarsAnimating(true);

      const timer = setInterval(() => {
        setDisplayStars(prev => {
          if (prev >= totalStars) {
            clearInterval(timer);
            return totalStars;
          }
          // Trigger pulse animation by changing key
          setStarsPulseKey(k => k + 1);

          return prev + 1;
        });
      }, 100); // Update interval for stars

      // Remove animation after it completes
      setTimeout(() => {
        setStarsAnimating(false);
      }, 800);

      return () => clearInterval(timer);
    }
  }, [displayScore, score, displayStars, totalStars]);

  const fetchConfig = async (companyGameId: number) => {
    try {
      const response = await fetch(`${API_URL}/api/config?companyGameId=${companyGameId}`, {
        headers: getAuthHeaders(),
      });

      // console.log('Config response status:', response.status);
      // console.log('Config response headers:', response.headers);

      if (!response.ok) {
        debugError('Config fetch failed with status:', response.status);
        const errorText = await response.text();
        debugError('Config fetch error response:', errorText);
        setLoading(false);
        return;
      }

      const responseData = await response.json();
      let data = responseData.data || responseData;

      // TEST debug override. REMOVE!
      // data = {
      //   "calendar": {
      //     "daysPerRow": 5,
      //     "description": "Sharpen your AI skills with a new challenge every day!",
      //     "month": 11,
      //     "startColumn": 4,
      //     "startDay": 1,
      //     "title": "AI Skill Advent Calendar",
      //     "year": 2025
      //   },
      //   "companyGameId": 100,
      //   "dayData": {
      //     "availableDays": [6, 7, 10, 11, 12, 13, 14, 17, 18, 19, 20, 21, 24, 25, 26, 27, 28, 101, 102, 103],
      //     "specialDays": {
      //       "7": {
      //         "count": 8,
      //         "type": "requireStars"
      //       },
      //       "14": {
      //         "count": 18,
      //         "type": "requireStars"
      //       },
      //       "21": {
      //         "count": 25,
      //         "type": "requireStars"
      //       },
      //       "28": {
      //         "count": 40,
      //         "type": "requireStars"
      //       },
      //       "27": {
      //         "type": "off",
      //         "reason": "🦃"
      //       }
      //     },
      //     "message": "Success"
      //   }
      // };

      debugLog(1, '[App] Fetched config:', data);

      setConfig(data);

      // Initialize theme after config is loaded
      try {
        const skinName = data.calendar?.skin || DEFAULT_SKIN;

        // Check if skin has changed - only reload if different from current
        if (currentSkin === skinName) {
          debugLog(1, `[App] Skin unchanged (${skinName}), skipping theme reload`);
          setThemeLoaded(true);
        } else {
          debugLog(1, `[App] Skin changed from ${currentSkin} to ${skinName}, loading theme`);
          await initializeTheme(skinName);
          debugLog(1, '[App] Theme initialized successfully');
          setThemeLoaded(true);
          setCurrentSkin(skinName);
        }
      } catch (error) {
        debugError('[App] Failed to load theme:', error);
        setThemeError(error instanceof Error ? error.message : 'Unknown error');
        // Still set themeLoaded to true to allow app to render (with fallback styles)
        setThemeLoaded(true);
      }

      setLoading(false);
    } catch (error) {
      debugError('Error fetching config:', error);
      setLoading(false);
      // Set themeLoaded even on error so app doesn't hang
      setThemeLoaded(true);
    }
  };

  const handleDayClick = async (day: number, isUnlocked: boolean, isCurrentDay: boolean) => {
    if (!isUnlocked) return;

    // Track modal open event
    trackModalOpen({
      day,
      today: isCurrentDay,
      best_day_score: getDayScore(day),
      best_day_stars: getDayStars(day),
      total_score: getScore(),
      total_stars: getTotalStars()
    });

    // Show modal overlay immediately
    setSelectedDay(day);
    setIsSelectedDayCurrentDay(isCurrentDay);

    // Check cache first
    const cachedContent = contentCacheRef.current.get(day);
    if (cachedContent) {
      debugLog(1, `[App] Using cached content for day ${day}`);
      setDayContent(cachedContent);
      return;
    }

    // Clear previous content if not cached
    setDayContent(null);

    try {
      const response = await fetch(`${API_URL}/api/content`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          day: day,
          companyGameId: config?.companyGameId
        }),
      });

      // Check for authentication errors
      if (response.status === 401 || response.status === 403) {
        debugError('[Auth] Token expired or invalid, logging out...');
        handleLogout();
        return;
      }

      if (response.ok) {
        const responseData = await response.json();
        debugLog(1, '[App] Day content response:', responseData);

        // Extract the actual day content from the nested structure
        let data = responseData.data || responseData;

        // TEST OVERRIDE remove!!
        // data = {
        //   "content": "## Ask AI for ideas and pick your favorite.\r\n\r\nAsk AI to brainstorm 10 ideas for a weekend activity or side project you are passionate about. Choose your favorite and explain why.\r\n\r\n**💡 Tip:** \"Request Multiple Versions.\" Ask the AI to explore the idea from a few different angles, such as one fun, one realistic, and one bold, so you can compare and choose the version that inspires you most.",
        //   "contentFlags": {
        //     "files": false,
        //     "promptUsed": true,
        //     "response": true,
        //     "transcript": false
        //   },
        //   "contentType": "markdown",
        //   "day": 6,
        //   "title": "Idea Brainstorm",
        //   "videos": [
        //     {
        //       "title": "Clip 1",
        //       "video_url": "https://developer.mozilla.org/shared-assets/videos/flower.mp4",
        //     },
        //     {
        //       "title": "Clip 2",
        //       "video_url": "https://developer.mozilla.org/shared-assets/videos/flower.mp4",
        //     }
        //   ]
        // };

        // TEST OVERRIDE for transcript contentType
        // data.contentFlags = { files: false, promptUsed: false, response: false, transcript: true };

        // Cache the content for this session
        contentCacheRef.current.set(day, data);

        setDayContent(data);
      } else {
        debugError('Failed to fetch day content');
        // Close modal on error
        setSelectedDay(null);
      }
    } catch (error) {
      debugError('Error fetching day content:', error);
      // Close modal on error
      setSelectedDay(null);
    }
  };

  const handleCloseModal = () => {
    setSelectedDay(null);
    setDayContent(null);
    setShouldCloseModal(false);
  };

  const handleCorrectAnswer = (bonusData: BonusData) => {
    // Store score popup data and show it
    setScorePopupData(bonusData);
    setScorePopupDay(selectedDay);
    setScorePopupToday(isSelectedDayCurrentDay);
    setShowScorePopup(true);

    // Close the day modal immediately
    setShouldCloseModal(true);

    // Only trigger confetti if points were actually added
    if (bonusData.addedPoints && bonusData.addedPoints > 0) {
      setTimeout(() => {
        setConfettiKey(prev => prev + 1); // Increment to trigger new confetti
      }, 500);
    }
  };

  const handleScorePopupContinue = () => {
    setIsScorePopupClosing(true);

    // Check for new badges
    let earnedBadges: NewBadge[] = [];
    if (config?.dayData.availableDays) {
      // Calculate taskDays (exclude "off" days from streak calculations)
      const taskDays = config.dayData.availableDays.filter(day => {
        const specialDay = config.dayData.specialDays[day];
        return specialDay?.type !== 'off';
      });

      const badgeResult = checkForNewBadges(taskDays.length, taskDays);
      if (badgeResult.newBadges.length > 0) {
        earnedBadges = badgeResult.newBadges;

        // Track each newly earned badge
        earnedBadges.forEach(badge => {
          trackBadgeEarned({
            badge_type: badge.type,
            badge_level: badge.tier,
            total_score: getScore(),
            total_stars: getTotalStars()
          });
        });
      }
    }

    // Trigger score animation in header
    if (scorePopupData && scorePopupData.scoreDelta && scorePopupData.scoreDelta > 0) {
      handleScoreAnimationStart(earnedBadges);
    }

    // Close score popup
    setTimeout(() => {
      setShowScorePopup(false);
      setIsScorePopupClosing(false);
      setScorePopupData(null);
      setScorePopupDay(null);
      setScorePopupToday(false);

      // If no badges, update score immediately and dispatch scoring complete event
      if (earnedBadges.length === 0) {
        setScore(getScore());
        setTotalStars(getTotalStars());

        // Dispatch event to notify that scoring sequence is complete (no badges case)
        debugLog(1, '[App] No badges earned - dispatching scoringComplete');
        window.dispatchEvent(new CustomEvent('scoringComplete'));
      }
    }, 300);
  };

  const handleScoreAnimationStart = (badges?: NewBadge[]) => {
    // Store badges if provided
    if (badges && badges.length > 0) {
      setNewBadges(badges);
      // Show badge popup immediately
      setShowBadgePopup(true);
    } else {
      // No badges - update score and stars immediately (existing behavior)
      setScore(getScore());
      setTotalStars(getTotalStars());
      setScoreAnimating(true);

      // Remove animation class after it completes
      setTimeout(() => {
        setScoreAnimating(false);

        // After score animation completes, dispatch scoring complete event
        debugLog(1, '[App] Score animation complete (no badges) - dispatching scoringComplete');
        window.dispatchEvent(new CustomEvent('scoringComplete'));
      }, 800);
    }
  };

  const handleBadgePopupContinue = () => {
    setIsBadgePopupClosing(true);

    // Wait for badge popup animation to complete
    setTimeout(() => {
      setShowBadgePopup(false);
      setIsBadgePopupClosing(false);

      // Step 1: Update and animate score
      setScore(getScore());
      setScoreAnimating(true);

      // Remove score animation class after it completes
      setTimeout(() => {
        setScoreAnimating(false);
      }, 800);

      // Step 2: After score finishes animating, update stars
      // (The existing useEffect will handle stars animation when displayScore catches up)
      setTimeout(() => {
        setTotalStars(getTotalStars());
      }, 800);

      if (newBadges.length > 0) {
        saveBadges(newBadges);
        // Set badges to animate (this will trigger BadgeSlots to re-render with animation)
        setBadgesToAnimate(newBadges);
        // Trigger badges section animation
        setBadgesAnimating(true);

        // Clear animation badges and section animation after animation completes
        setTimeout(() => {
          setBadgesToAnimate([]);
          setNewBadges([]); // Clear badges
          setBadgesAnimating(false);
        }, 800); // Match the badgeEarn animation duration
      }

      // Dispatch event to notify that scoring sequence is complete and calendar should check for new unlocks
      window.dispatchEvent(new CustomEvent('scoringComplete'));

    }, 300);
  };

  const handleClearCompletions = () => {
    if (window.confirm('Are you sure you want to clear all completion data? This cannot be undone.')) {
      clearAllCompletions();
      contentCacheRef.current.clear(); // Clear session cache
      localStorage.removeItem(THEME_STORAGE_KEY); // Clear theme cache
      debugLog(1, '[App] Cleared content cache');
      window.location.reload(); // Reload to update the calendar display
    }
  };

  const handleInfoPopupContinue = () => {
    setIsInfoPopupClosing(true);

    // Mark the current popup type as shown
    if (infoPopupType === 'welcome') {
      localStorage.setItem(INFO_POPUP_WELCOME_KEY, 'true');
    } else if (infoPopupType === 'lastDay') {
      localStorage.setItem(INFO_POPUP_LAST_DAY_KEY, 'true');
    }

    // Close info popup
    setTimeout(() => {
      setShowInfoPopup(false);
      setIsInfoPopupClosing(false);

      // Dispatch event to notify that info popup is closed and calendar should check for new unlocks
      window.dispatchEvent(new CustomEvent('infoPopupClosed'));
    }, 300);
  };

  const handleLogout = () => {
    debugLog(1, '[App] User logged out, resetting auth state...');
    setIsAuthenticated(false);
    setAuthUser(null);
    setConfig(null);
    setLoading(true);
  };

  const handleToggleMute = () => {
    const newMutedState = !muted;

    if (newMutedState) {
      // About to mute - play sound first while still unmuted
      // sound.clickOpen();
      setMuted(newMutedState);
      setMutedState(newMutedState);
    } else {
      // About to unmute - toggle state first, then play sound
      setMuted(newMutedState);
      setMutedState(newMutedState);
      sound.clickOpen();
    }
  };

  const handleOpenLinkInventory = async () => {
    sound.clickOpen();

    // Check cache first
    if (videosCacheRef.current) {
      debugLog(1, '[App] Using cached videos for LinkInventory');
      setLinkInventoryVideos(videosCacheRef.current);
      setShowLinkInventory(true);
      return;
    }

    // Show loading state
    setLinkInventoryLoading(true);
    setShowLinkInventory(true);

    // TEST long wait
    // await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      const response = await fetch(`${API_URL}/api/company-games/${config?.companyGameId}/videos`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const responseData = await response.json();
        const data = responseData.data || responseData;
        const fetchedVideos = data || [];

        debugLog(1, '[App] Fetched videos for LinkInventory:', fetchedVideos);

        // Cache the videos for the session
        videosCacheRef.current = fetchedVideos;
        setLinkInventoryVideos(fetchedVideos);
      } else {
        console.error('Failed to fetch videos');
        setShowLinkInventory(false);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      setShowLinkInventory(false);
    } finally {
      setLinkInventoryLoading(false);
    }
  };

  const handleCloseLinkInventory = () => {
    setShowLinkInventory(false);
    setLinkInventoryVideos(null);
  };

  const handleSelectGame = (companyGameId: number) => {
    debugLog(1, '[App] Game selected:', companyGameId);
    setSelectedCompanyGameId(companyGameId);
    setShowChoiceScreen(false);
    // No need to set loading - top bar stays visible, main content shows "Loading calendar..."

    // Push browser history state for back button support
    const currentUrl = window.location.href.split('#')[0];
    window.history.pushState({ view: 'calendar', gameId: companyGameId }, '', `${currentUrl}#calendar${companyGameId}`);
    hasBrowserHistory.current = true;
  };

  // Shared function to return to choice screen (used by both manual navigation and browser back)
  const returnToChoiceScreen = useCallback(async () => {
    debugLog(1, '[App] Returning to choice screen');

    // Clear hash from URL when returning to choice screen
    const currentUrl = window.location.href.split('#')[0];
    window.history.replaceState(null, '', currentUrl);

    // Reload theme to match first calendar's skin (for consistent choice screen styling)
    if (companyGames.length > 0) {
      const choiceScreenSkin = companyGames[0].skin || DEFAULT_SKIN;
      if (currentSkin !== choiceScreenSkin) {
        debugLog(1, `[App] Reloading theme for choice screen: ${choiceScreenSkin}`);
        try {
          await initializeTheme(choiceScreenSkin);
          setCurrentSkin(choiceScreenSkin);
        } catch (error) {
          debugError('[App] Failed to reload theme for choice screen:', error);
        }
      } else {
        debugLog(1, '[App] Choice screen skin unchanged, keeping current theme');
      }
    }

    // Clear selected game and config, show choice screen again
    // Top bar stays visible throughout
    setSelectedCompanyGameId(null);
    setConfig(null);
    setShowChoiceScreen(true);
    hasBrowserHistory.current = false;
  }, [companyGames, currentSkin]);

  const handleBackToMenu = () => {
    // If we have browser history, use browser back button (which will trigger popstate)
    // Otherwise, directly call the return function
    if (hasBrowserHistory.current) {
      debugLog(1, '[App] Using browser back button');
      window.history.back();
    } else {
      debugLog(1, '[App] No browser history, directly returning to choice screen');
      returnToChoiceScreen();
    }
  };

  // Update popstate handler when returnToChoiceScreen changes
  useEffect(() => {
    const handlePopState = () => {
      debugLog(1, '[App] Browser navigation detected');
      const hash = window.location.hash;

      if (hash.startsWith('#calendar')) {
        // Extract game ID from hash (e.g., #calendar1 -> 1)
        const gameIdStr = hash.replace('#calendar', '');
        const gameId = parseInt(gameIdStr, 10);

        if (!isNaN(gameId) && gameId > 0) {
          debugLog(1, `[App] Opening calendar ${gameId} from URL`);
          setSelectedCompanyGameId(gameId);
          setShowChoiceScreen(false);
          hasBrowserHistory.current = true;
        } else {
          // Invalid game ID, return to choice screen
          debugLog(1, '[App] Invalid calendar ID in hash, returning to choice screen');
          returnToChoiceScreen();
        }
      } else {
        // No calendar hash, return to choice screen
        debugLog(1, '[App] No calendar in hash, returning to choice screen');
        returnToChoiceScreen();
      }
    };

    // Remove old listener and add new one with updated returnToChoiceScreen
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [returnToChoiceScreen]);

  // Show nothing while checking auth
  if (authChecking) {
    return null;
  }

  // Show login popup if not authenticated
  if (!isAuthenticated) {
    return <LoginPopup />;
  }

  // If we're showing the choice screen, wait for theme to load first
  if (showChoiceScreen) {
    // Show loading screen while theme is loading
    if (!themeLoaded) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: 'linear-gradient(to bottom, #5aaeec 0%, #7ac1f5 100%)',
          color: 'white',
          fontSize: '1.5rem',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
        }}>
          Loading theme...
        </div>
      );
    }

    // Render choice screen after theme is loaded
    return (
      <div className="App">
        <header className="App-top-bar">
          <div className="top-bar-content">
            <div className="top-bar">
              <div className="top-bar-content">
                <div className="top-bar-spacer-cell">
                  <button
                    className="mute-button"
                    onClick={handleToggleMute}
                    title={muted ? "Unmute sounds" : "Mute sounds"}
                    aria-label={muted ? "Unmute sounds" : "Mute sounds"}
                  >
                    <img src={muted ? "/mute.svg" : "/unmute.svg"} alt="" />
                  </button>
                  <button
                    className="link-inventory-button"
                    onClick={handleOpenLinkInventory}
                    title="Knowledge Hub"
                    aria-label="Knowledge Hub"
                  >
                    <img src="/knowledge.svg" alt="" />
                    <span className="link-inventory-text">Knowledge Hub</span>
                  </button>
                </div>
                <div className="top-bar-avatar-cell">
                  {authUser && <UserAvatar user={authUser} onLogout={handleLogout} />}
                </div>
              </div>
            </div>
          </div>
        </header>
        <main>
          <div className="main-content">
            <CalendarChoiceScreen
              companyGames={companyGames}
              onSelectGame={handleSelectGame}
              apiUrl={API_URL}
            />
          </div>
        </main>
      </div>
    );
  }

  // From here on, we need theme for the calendar view (config can load after)
  // Show loading screen while theme is loading
  if (!themeLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(to bottom, #5aaeec 0%, #7ac1f5 100%)',
        color: 'white',
        fontSize: '1.5rem',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        Loading theme...
      </div>
    );
  }

  // Show error if theme failed to load (but still render app with fallback)
  if (themeError) {
    console.warn('[App] Theme loading error (continuing with fallback):', themeError);
  }

  // Render app with top bar (config can be loading)
  return (
    <div className="App">
      <Confetti key={confettiKey} trigger={confettiKey > 0} />
      <header className="App-top-bar">
        <div className="top-bar-content">
          <div className="top-bar">
            <div className="top-bar-content">
              <div className="top-bar-spacer-cell">
                <button
                  className="mute-button"
                  onClick={handleToggleMute}
                  title={muted ? "Unmute sounds" : "Mute sounds"}
                  aria-label={muted ? "Unmute sounds" : "Mute sounds"}
                >
                  <img src={muted ? "/mute.svg" : "/unmute.svg"} alt="" />
                </button>
                <button
                  className="link-inventory-button"
                  onClick={handleOpenLinkInventory}
                  title="Knowledge Hub"
                  aria-label="Knowledge Hub"
                >
                  <img src="/knowledge.svg" alt="" />
                  <span className="link-inventory-text">Knowledge Hub</span>
                </button>
              </div>
              <div className="top-bar-avatar-cell">
                {authUser && (
                  <UserAvatar
                    user={authUser}
                    onLogout={handleLogout}
                    showBackToMenu={hasChoiceScreen}
                    onBackToMenu={handleBackToMenu}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <main>
        {!config ? (
          <div className="main-content">
            <div className="app-loading">Loading calendar...</div>
          </div>
        ) : !config.calendar ? (
          <div className="main-content">
            <div className="app-loading">Error: Invalid configuration received from server</div>
          </div>
        ) : (
          <div className="main-content">
            <div className="header-bar">
              <div className="header-bar-content">
                <div className="header-text">
                  <h1>{config.calendar.title}</h1>
                  <p>{config.calendar.description}</p>
                </div>
                <div className="score-container">
                  <div className="score-display">
                    <div className={`score-section-score ${scoreAnimating ? 'animating' : ''}`}>
                      <div className="score-label">Score</div>
                      <div className="header-score-value">{displayScore.toLocaleString()}</div>
                    </div>
                    <div className={`score-section-stars ${starsAnimating ? 'animating' : ''}`}>
                      <div className="score-label">Stars</div>
                      <div key={starsPulseKey} className="stars-display pulsing">
                        <StarIcon filled={true} size={36} />
                        <span className="star-count">{displayStars}</span>
                      </div>
                    </div>
                    <div className={`score-section-badges ${badgesAnimating ? 'animating' : ''}`}>
                      <div className="score-label">Badges</div>
                      <BadgeSlots animateBadges={badgesToAnimate} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="calendar-content">
              {serverDate ? (
                <CalendarGrid
                  startDay={config.calendar.startDay}
                  startColumn={config.calendar.startColumn || 1}
                  year={config.calendar.year}
                  month={config.calendar.month}
                  daysPerRow={config.calendar.daysPerRow}
                  availableDays={config.dayData.availableDays}
                  specialDays={config.dayData.specialDays}
                  onDayClick={handleDayClick}
                  serverDate={serverDate}
                />
              ) : (
                <div className="app-loading">Loading calendar...</div>
              )}
            </div>
          </div>
        )}
      </main>
      {selectedDay && (
        <DayModal
          dayContent={dayContent}
          dayType={config?.dayData.specialDays[selectedDay]?.type || 'normal'}
          companyGameId={config?.companyGameId}
          totalAvailableDays={config?.dayData.availableDays.length}
          availableDays={config?.dayData.availableDays}
          onClose={handleCloseModal}
          isCurrentDay={isSelectedDayCurrentDay}
          onServerAnswer={handleCorrectAnswer}
          onScoreAnimationStart={handleScoreAnimationStart}
          shouldClose={shouldCloseModal}
          loading={!dayContent}
          chatSession={selectedDay ? chatSessions[selectedDay] : undefined}
          onChatSessionUpdate={(session) => {
            if (selectedDay) {
              setChatSessions(prev => ({
                ...prev,
                [selectedDay]: session
              }));
            }
          }}
          onChatSessionReset={() => {
            if (selectedDay) {
              setChatSessions(prev => {
                const newSessions = { ...prev };
                delete newSessions[selectedDay];
                return newSessions;
              });
            }
          }}
        />
      )}
      <button
        className="clear-storage-button"
        onClick={handleClearCompletions}
        title="Clear all completion data"
        aria-label="Clear completion data"
      />
      {showScorePopup && scorePopupData && scorePopupDay !== null && (
        <ScorePopup
          bonusData={scorePopupData}
          day={scorePopupDay}
          today={scorePopupToday}
          onContinue={handleScorePopupContinue}
          isClosing={isScorePopupClosing}
        />
      )}
      {showBadgePopup && (
        <BadgePopup
          badges={newBadges}
          onContinue={handleBadgePopupContinue}
          isClosing={isBadgePopupClosing}
        />
      )}
      {showInfoPopup && (
        <InfoPopup
          type={infoPopupType}
          onContinue={handleInfoPopupContinue}
          isClosing={isInfoPopupClosing}
        />
      )}
      {showLinkInventory && (
        <LinkInventory
          videos={linkInventoryVideos}
          onClose={handleCloseLinkInventory}
          loading={linkInventoryLoading}
        />
      )}
    </div>
  );
}

export default App;
